import React, { useState, useMemo, useCallback, useRef } from 'react';
import Plot from 'react-plotly.js';
import { SENSOR_TYPES, DATE_AXIS_TICK_FORMAT_STOPS, toEasternPlotDate } from '../../../utils/sensorConfig';
import { buildArrowBaselineTimeSeries, buildCompassRose, computeArrowResolution } from '../../../utils/windVectorUtils';
import { bucketMinutesForRange } from '../../../hooks/useWaterQualityData';

const WAVE_SENSORS = ['wind_speed', 'wind_direction_true', 'wave_height', 'wave_direction'];

export default function WaveChart({ transformedData, rangeKey, rangeLabel, loadingPriority3, xRange }) {
  const available = useMemo(() => WAVE_SENSORS.filter(t => transformedData[t]?.length), [transformedData]);
  const [selected, setSelected] = useState(() => available[0] || 'wave_height');
  const activeSensor = available.includes(selected) ? selected : (available[0] || selected);

  const [arrowResMin, setArrowResMin] = useState(() => bucketMinutesForRange(rangeKey));
  const rangeKeyRef = useRef(rangeKey);
  rangeKeyRef.current = rangeKey;

  const isDirectionSensor = activeSensor === 'wind_direction_true' || activeSensor === 'wave_direction';

  const vectorData = useMemo(() => {
    if (!isDirectionSensor) return null;
    const isWind = activeSensor === 'wind_direction_true';
    return buildArrowBaselineTimeSeries(
      transformedData,
      isWind ? 'wind_direction_true' : 'wave_direction',
      isWind ? 'wind_speed'          : 'wave_height',
      { resolutionMin: arrowResMin, applyCalm: isWind }
    );
  }, [transformedData, activeSensor, isDirectionSensor, arrowResMin]);

  const lineData = useMemo(() => {
    if (vectorData) return null;
    const raw = (transformedData[activeSensor] || []).slice().reverse();
    const chartData = activeSensor === 'wave_height'
      ? raw.map(r => ({ x: toEasternPlotDate(r.time), y: (r.sensor_value || 0) * 100 }))
      : raw.map(r => ({ x: toEasternPlotDate(r.time), y: r.sensor_value || 0 }));
    return [{
      x: chartData.map(d => d.x), y: chartData.map(d => d.y),
      type: 'scatter', mode: 'lines',
      line: { color: SENSOR_TYPES[activeSensor]?.color, width: 2 },
      name: SENSOR_TYPES[activeSensor]?.name,
      hovertemplate: `%{x|%Y-%m-%d %H:%M}<br>%{y:.2f} ${SENSOR_TYPES[activeSensor]?.unit || ''}<extra></extra>`,
    }];
  }, [transformedData, activeSensor, vectorData]);

  const onRelayout = useCallback((ev) => {
    const x0 = ev?.['xaxis.range[0]'] || ev?.['xaxis.range']?.[0];
    const x1 = ev?.['xaxis.range[1]'] || ev?.['xaxis.range']?.[1];
    if (x0 && x1) {
      const ms = new Date(x1) - new Date(x0);
      setArrowResMin(prev => Math.min(computeArrowResolution(ms), bucketMinutesForRange(rangeKeyRef.current)));
    }
  }, []);

  if (!available.length) return null;

  const compass = buildCompassRose('tr');
  const st = SENSOR_TYPES[activeSensor] || {};

  return (
    <div style={{ background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,.03)', border: '1px solid rgba(0,0,0,.04)', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0 }}>
          Wave Trends (Past {rangeLabel})
        </h3>
        {loadingPriority3 && <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #e2e8f0', borderTopColor: '#64748b', animation: 'spin 0.8s linear infinite' }} />}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
        {available.map(t => (
          <button key={t} onClick={() => setSelected(t)} style={{
            padding: '6px 14px', borderRadius: 100, fontSize: 13, cursor: 'pointer',
            fontWeight: activeSensor === t ? 600 : 400,
            background: activeSensor === t ? '#1e293b' : '#fff',
            color: activeSensor === t ? '#fff' : '#64748b',
            border: activeSensor === t ? 'none' : '1px solid #e2e8f0',
          }}>{SENSOR_TYPES[t]?.name || t}</button>
        ))}
      </div>

      {vectorData ? (
        <Plot
          data={vectorData.traces}
          layout={{
            height: 320,
            xaxis: { type: 'date', tickformatstops: DATE_AXIS_TICK_FORMAT_STOPS, ...(xRange && { range: xRange }) },
            yaxis: { showticklabels: false, range: [0, 2], fixedrange: true },
            annotations: [...vectorData.annotations, ...compass.annotations],
            shapes: [...compass.shapes],
            margin: { l: 40, r: 40, t: 20, b: 80 },
            showlegend: false, hovermode: 'closest',
            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
          }}
          onRelayout={onRelayout}
          config={{ displayModeBar: false, responsive: true }}
          style={{ width: '100%' }}
        />
      ) : (
        <Plot
          data={lineData}
          layout={{
            height: 320, margin: { b: 80 },
            xaxis: { type: 'date', tickformatstops: DATE_AXIS_TICK_FORMAT_STOPS, ...(xRange && { range: xRange }) },
            yaxis: { title: { text: `${st.name} (${st.unit})` }, automargin: true },
            paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)', hovermode: 'x unified',
          }}
          config={{ displayModeBar: false, responsive: true }}
          style={{ width: '100%' }}
        />
      )}
    </div>
  );
}
